<template>
    <div class="validation-justificatif-vague">
        <div class="text-h6 mb-3">Coureurs en attente de vérification</div>
        <v-alert border-color="primary" border="start" dense v-if="pending.length === 0">
            Il n'y a aucun coureur en attente de vérification.
        </v-alert>
        <div v-else>
            <v-alert border-color="primary" border="start" dense class="mb-3">
                Il y a <b>{{ pending.length }} coureur(s)</b> en attente de vérification.
            </v-alert>
            <v-table>
                <thead>
                    <tr>
                        <th class="text-left">Nom - Prénom</th>
                        <th class="text-left">Sas / vague demandé</th>
                        <th class="text-left">Document</th>
                        <th class="text-left">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="inscription in pending" :key="'pending-' + inscription.id">
                        <td>{{ inscription.nom }} {{ inscription.prenom }}</td>
                        <td>{{ get_vague_label(inscription) }}</td>
                        <td>
                            <a :href="get_document_url(inscription)" target="_blank" rel="noopener noreferrer">
                                <v-btn prepend-icon="mdi-file-eye-outline" density="compact" color="primary-dark"
                                    variant="elevated" elevation="0">Consulter</v-btn>
                            </a>
                        </td>
                        <td>
                            <v-btn class="mx-1" color="green-darken-4" density="compact" variant="elevated"
                                elevation="0" @click="openDialogAccept(inscription)">Accepter</v-btn>
                            <v-btn class="mx-1" color="red-darken-4" density="compact" variant="elevated" elevation="0"
                                @click="openDialogReject(inscription)">Refuser</v-btn>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <div class="text-h6 mt-8 mb-3">Coureurs déjà traités</div>
        <v-alert border-color="primary" border="start" dense v-if="treated.length === 0">
            Aucun coureur traité pour le moment.
        </v-alert>
        <div v-else>
            <v-table>
                <thead>
                    <tr>
                        <th class="text-left">Nom - Prénom</th>
                        <th class="text-left">Sas / vague</th>
                        <th class="text-left">Document</th>
                        <th class="text-left">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="inscription in treated" :key="'treated-' + inscription.id">
                        <td>{{ inscription.nom }} {{ inscription.prenom }}</td>
                        <td>{{ get_vague_label(inscription) }}</td>
                        <td>
                            <a :href="get_document_url(inscription)" target="_blank" rel="noopener noreferrer">
                                <v-btn prepend-icon="mdi-file-eye-outline" density="compact" color="primary-dark"
                                    variant="elevated" elevation="0">Consulter</v-btn>
                            </a>
                        </td>
                        <td>
                            <v-btn color="green-darken-4" density="compact" variant="elevated" elevation="0"
                                @click="openDialogChangeVague(inscription)">Modifier la vague</v-btn>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>

        <v-dialog v-model="show_accept" width="500" max-width="100%" scroll-strategy="none">
            <v-card>
                <v-card-text>
                    <div class="text-body-1">Voulez-vous vraiment <b>accepter</b> le justificatif de
                        {{ selected_inscription?.prenom }} {{ selected_inscription?.nom }} ?</div>
                    <div>Le coureur recevra un e-mail de confirmation et conservera sa vague actuelle.</div>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="red-darken-4" @click="show_accept = false">Non</v-btn>
                    <v-btn color="green-darken-4" :loading="loading" @click="handle_accept">Oui</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="show_reject" width="500" max-width="100%" scroll-strategy="none">
            <v-card>
                <v-card-text>
                    <div class="text-body-1">Voulez-vous vraiment <b>refuser</b> le justificatif de
                        {{ selected_inscription?.prenom }} {{ selected_inscription?.nom }} ?</div>
                    <div class="mb-3">Choisissez la vague de destination :</div>
                    <v-select v-model="selected_vague_id" :items="vague_options" item-title="nom" item-value="id"
                        label="Nouvelle vague" density="compact" hide-details></v-select>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="red-darken-4" @click="closeRejectDialog">Annuler</v-btn>
                    <v-btn color="green-darken-4" :loading="loading" :disabled="!selected_vague_id"
                        @click="handle_reject">Confirmer</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="show_change_vague" width="500" max-width="100%" scroll-strategy="none">
            <v-card>
                <v-card-text>
                    <div class="text-body-1">Changer la vague de {{ selected_inscription?.prenom }}
                        {{ selected_inscription?.nom }}</div>
                    <div class="mb-3">Choisissez la nouvelle vague :</div>
                    <v-select v-model="selected_vague_id" :items="vague_options" item-title="nom" item-value="id"
                        label="Nouvelle vague" density="compact" hide-details></v-select>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="red-darken-4" @click="closeChangeDialog">Annuler</v-btn>
                    <v-btn color="primary-dark" :loading="loading" :disabled="!selected_vague_id"
                        @click="handle_change_vague">Confirmer</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
import AdminService from "@/services/admin.service.js";

export default {
    name: "ValidationJustificatifVague",
    data() {
        return {
            pending: [],
            treated: [],
            vagues_by_course: {},
            selected_inscription: null,
            selected_vague_id: null,
            show_accept: false,
            show_reject: false,
            show_change_vague: false,
            loading: false,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        vague_options() {
            if (!this.selected_inscription) {
                return [];
            }
            const id_course = this.selected_inscription.id_course;
            const current_vague = parseInt(this.selected_inscription.id_vague_course, 10);
            const vagues = this.vagues_by_course[id_course] || this.vagues_by_course[String(id_course)] || [];
            return vagues.filter((v) => parseInt(v.id, 10) !== current_vague);
        },
    },
    methods: {
        get_vague_label(inscription) {
            if (inscription.vague) {
                return inscription.vague;
            }
            return inscription.id_vague_course ? "Vague #" + inscription.id_vague_course : "—";
        },
        get_document_url(inscription) {
            return inscription.champs?.justificatif_vague_document || "";
        },
        openDialogAccept(inscription) {
            this.selected_inscription = inscription;
            this.show_accept = true;
        },
        openDialogReject(inscription) {
            this.selected_inscription = inscription;
            this.selected_vague_id = null;
            this.show_reject = true;
            this.$nextTick(() => {
                if (this.vague_options.length === 1) {
                    this.selected_vague_id = this.vague_options[0].id;
                }
            });
        },
        openDialogChangeVague(inscription) {
            this.selected_inscription = inscription;
            this.selected_vague_id = null;
            this.show_change_vague = true;
            this.$nextTick(() => {
                if (this.vague_options.length === 1) {
                    this.selected_vague_id = this.vague_options[0].id;
                }
            });
        },
        closeRejectDialog() {
            this.show_reject = false;
            this.selected_vague_id = null;
        },
        closeChangeDialog() {
            this.show_change_vague = false;
            this.selected_vague_id = null;
        },
        async handle_accept() {
            this.loading = true;
            const request = await AdminService.postValidationJustificatifVague({
                id: this.selected_inscription.id,
                action: "accept",
            });
            this.loading = false;
            this.show_accept = false;
            if (request === true) {
                await this.fetch_lists();
            }
        },
        async handle_reject() {
            this.loading = true;
            const request = await AdminService.postValidationJustificatifVague({
                id: this.selected_inscription.id,
                action: "reject",
                id_vague_course: this.selected_vague_id,
            });
            this.loading = false;
            this.closeRejectDialog();
            if (request === true) {
                await this.fetch_lists();
            }
        },
        async handle_change_vague() {
            this.loading = true;
            const request = await AdminService.postValidationJustificatifVague({
                id: this.selected_inscription.id,
                action: "change_vague",
                id_vague_course: this.selected_vague_id,
            });
            this.loading = false;
            this.closeChangeDialog();
            if (request === true) {
                await this.fetch_lists();
            }
        },
        async fetch_lists() {
            const request = await AdminService.getValidationJustificatifVague(this.mainStore.module.id);
            if (request && !request.error) {
                this.pending = request.pending || [];
                this.treated = request.treated || [];
                this.vagues_by_course = request.vagues_by_course || {};
            }
        },
    },
    created() {
        this.fetch_lists();
    },
};
</script>
